/**
 * Admin Rate Limiting
 * Comprehensive rate limit policy management with analytics and monitoring
 */
import React, { useEffect, useState } from 'react'
import { adminApiClient as api } from '../../services/api'
import { useToast } from '../../hooks/useToast'

type Policy = {
  id?: string
  scope_type: 'global' | 'route'
  route?: string | null
  identifier: 'ip' | 'user' | 'api_key'
  limit: string
  enabled: boolean
  updated_at?: string | null
}

type EventRow = {
  occurred_at: string
  route: string | null
  identifier: string
  client_key: string | null
  limit: string | null
  remote_addr: string | null
  status: string
}

type StatRow = {
  route: string | null
  identifier: string
  hits: number
}

export const RateLimiting: React.FC = () => {
  const { toast } = useToast()
  const [policies, setPolicies] = useState<Policy[]>([])
  const [events, setEvents] = useState<EventRow[]>([])
  const [stats, setStats] = useState<StatRow[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [timeRange, setTimeRange] = useState(120) // minutes
  const [showAddRoute, setShowAddRoute] = useState(false)
  const [newRoutePolicy, setNewRoutePolicy] = useState<Partial<Policy>>({
    scope_type: 'route',
    route: '',
    identifier: 'ip',
    limit: '60/minute',
    enabled: true
  })

  const fetchData = async () => {
    setLoading(true)
    try {
      const [cfg, ev, st] = await Promise.all([
        api.get<Policy[]>('/admin/rate-limits/config'),
        api.get<EventRow[]>(`/admin/rate-limits/events?since_minutes=${timeRange}&limit=200`),
        api.get<StatRow[]>(`/admin/rate-limits/stats?since_minutes=${timeRange}`)
      ])
      setPolicies(cfg.data)
      setEvents(ev.data)
      setStats(st.data)
    } catch (e: any) {
      toast({
        title: 'Failed to load',
        description: e.message || 'Rate limiting API unavailable',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [timeRange])

  const addGlobalPolicy = async () => {
    setSaving(true)
    try {
      const body: Policy = {
        scope_type: 'global',
        identifier: 'ip',
        limit: '200/minute',
        enabled: true
      }
      await api.put('/admin/rate-limits/config', body)
      toast({ title: 'Saved', description: 'Global policy created', variant: 'success' })
      await fetchData()
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' })
    } finally { setSaving(false) }
  }

  const addRoutePolicy = async () => {
    if (!newRoutePolicy.route?.trim()) {
      toast({ title: 'Validation error', description: 'Route is required', variant: 'destructive' })
      return
    }
    setSaving(true)
    try {
      await api.put('/admin/rate-limits/config', newRoutePolicy as Policy)
      toast({ title: 'Saved', description: 'Route policy created', variant: 'success' })
      setShowAddRoute(false)
      setNewRoutePolicy({
        scope_type: 'route',
        route: '',
        identifier: 'ip',
        limit: '60/minute',
        enabled: true
      })
      await fetchData()
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' })
    } finally { setSaving(false) }
  }

  const deletePolicy = async (id?: string) => {
    if (!id) return
    if (!confirm('Delete this policy?')) return
    setSaving(true)
    try {
      await api.delete(`/admin/rate-limits/config/${id}`)
      toast({ title: 'Deleted', description: 'Policy deleted', variant: 'success' })
      await fetchData()
    } catch (e: any) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' })
    } finally { setSaving(false) }
  }

  const savePolicy = async (p: Policy) => {
    setSaving(true)
    try {
      await api.put('/admin/rate-limits/config', p)
      toast({ title: 'Saved', description: 'Policy updated', variant: 'success' })
      await fetchData()
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' })
    } finally { setSaving(false) }
  }

  const totalHits = stats.reduce((sum, s) => sum + s.hits, 0)
  const topRoute = stats[0]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Rate Limiting</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage API rate limits and monitor 429 responses
          </p>
        </div>
        <div className="space-x-2">
          <button
            disabled={loading}
            className="btn"
            onClick={fetchData}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Total 429 Events</div>
          <div className="text-2xl font-semibold mt-1">{totalHits.toLocaleString()}</div>
          <div className="text-xs text-gray-400 mt-1">Last {timeRange} minutes</div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Active Policies</div>
          <div className="text-2xl font-semibold mt-1">
            {policies.filter(p => p.enabled).length}
            <span className="text-sm text-gray-400 font-normal ml-2">
              / {policies.length} total
            </span>
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {policies.filter(p => p.scope_type === 'global').length} global, {' '}
            {policies.filter(p => p.scope_type === 'route').length} route-specific
          </div>
        </div>

        <div className="bg-white border rounded-lg p-4">
          <div className="text-sm text-gray-500">Most Hit Route</div>
          <div className="text-lg font-semibold mt-1 truncate">
            {topRoute ? (topRoute.route || 'Global') : 'None'}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {topRoute ? `${topRoute.hits} hits (${topRoute.identifier})` : 'No events'}
          </div>
        </div>
      </div>

      {/* Time Range Selector */}
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm font-medium">Time Range:</label>
        <select
          className="input w-48"
          value={timeRange}
          onChange={(e) => setTimeRange(Number(e.target.value))}
        >
          <option value={15}>Last 15 minutes</option>
          <option value={60}>Last hour</option>
          <option value={120}>Last 2 hours</option>
          <option value={360}>Last 6 hours</option>
          <option value={1440}>Last 24 hours</option>
          <option value={10080}>Last 7 days</option>
        </select>
      </div>

      {/* Policies Section */}
      <section className="bg-white border rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Rate Limit Policies</h2>
          <div className="space-x-2">
            <button
              disabled={saving}
              className="btn btn-outline"
              onClick={() => setShowAddRoute(!showAddRoute)}
            >
              {showAddRoute ? 'Cancel' : '+ Add Route Policy'}
            </button>
            <button
              disabled={saving}
              className="btn btn-primary"
              onClick={addGlobalPolicy}
            >
              + Add Global Policy
            </button>
          </div>
        </div>

        {/* Add Route Policy Form */}
        {showAddRoute && (
          <div className="border rounded-lg p-4 mb-4 bg-gray-50">
            <h3 className="text-sm font-medium mb-3">New Route-Specific Policy</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-gray-600">Route Path</label>
                <input
                  className="input mt-1 w-full"
                  placeholder="/api/v1/chat"
                  value={newRoutePolicy.route || ''}
                  onChange={e => setNewRoutePolicy({...newRoutePolicy, route: e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Identifier</label>
                <select
                  className="input mt-1 w-full"
                  value={newRoutePolicy.identifier}
                  onChange={e => setNewRoutePolicy({...newRoutePolicy, identifier: e.target.value as any})}
                >
                  <option value="ip">IP Address</option>
                  <option value="user">User ID</option>
                  <option value="api_key">API Key</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600">Limit</label>
                <input
                  className="input mt-1 w-full"
                  placeholder="60/minute"
                  value={newRoutePolicy.limit || ''}
                  onChange={e => setNewRoutePolicy({...newRoutePolicy, limit: e.target.value})}
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Status</label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={newRoutePolicy.enabled}
                    onChange={e => setNewRoutePolicy({...newRoutePolicy, enabled: e.target.checked})}
                  />
                  <span className="text-sm">Enabled</span>
                </label>
              </div>
              <div className="flex items-end">
                <button
                  className="btn btn-primary w-full"
                  onClick={addRoutePolicy}
                  disabled={saving}
                >
                  Create Policy
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Policies List */}
        <div className="space-y-3">
          {policies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-2">No rate limit policies configured</p>
              <p className="text-sm">Create a global or route-specific policy to get started</p>
            </div>
          ) : (
            policies.map((p, idx) => (
              <div
                key={p.id || idx}
                className={`border rounded-lg p-4 ${!p.enabled ? 'bg-gray-50 opacity-60' : 'bg-white'}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div>
                      <label className="text-xs text-gray-600">Scope</label>
                      <select
                        className="input mt-1 w-full"
                        value={p.scope_type}
                        onChange={e => setPolicies(prev => prev.map((it,i)=> i===idx? {...it, scope_type: e.target.value as any}: it))}
                      >
                        <option value="global">Global</option>
                        <option value="route">Route-Specific</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Route Path</label>
                      <input
                        className="input mt-1 w-full"
                        placeholder={p.scope_type === 'global' ? 'N/A (global)' : '/api/v1/*'}
                        value={p.route || ''}
                        disabled={p.scope_type === 'global'}
                        onChange={e => setPolicies(prev => prev.map((it,i)=> i===idx? {...it, route: e.target.value}: it))}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Identifier</label>
                      <select
                        className="input mt-1 w-full"
                        value={p.identifier}
                        onChange={e => setPolicies(prev => prev.map((it,i)=> i===idx? {...it, identifier: e.target.value as any}: it))}
                      >
                        <option value="ip">IP Address</option>
                        <option value="user">User ID</option>
                        <option value="api_key">API Key</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Limit</label>
                      <input
                        className="input mt-1 w-full"
                        placeholder="60/minute"
                        value={p.limit}
                        onChange={e => setPolicies(prev => prev.map((it,i)=> i===idx? {...it, limit: e.target.value}: it))}
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={p.enabled}
                          onChange={e=> setPolicies(prev => prev.map((it,i)=> i===idx? {...it, enabled: e.target.checked}: it))}
                        />
                        <span className="text-sm">Enabled</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      className="btn btn-secondary px-4"
                      onClick={()=>savePolicy(p)}
                      disabled={saving}
                    >
                      Save
                    </button>
                    {p.id && (
                      <button
                        className="btn btn-outline px-4 text-red-600"
                        onClick={()=>deletePolicy(p.id)}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                {p.updated_at && (
                  <div className="text-xs text-gray-500 mt-2">
                    Updated: {new Date(p.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white border rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Hit Statistics</h2>
        {stats.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No rate limit hits in the selected time range</p>
            <p className="text-sm mt-1">This is good! Your rate limits are not being exceeded.</p>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-3 font-medium">Route</th>
                  <th className="p-3 font-medium">Identifier Type</th>
                  <th className="p-3 font-medium text-right">Total Hits</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-mono text-xs">
                      {s.route || <span className="text-gray-400 italic">Global</span>}
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        {s.identifier}
                      </span>
                    </td>
                    <td className="p-3 text-right font-semibold">{s.hits.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Events Section */}
      <section className="bg-white border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">Recent 429 Events</h2>
        {events.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No rate limit events in the last {timeRange} minutes</p>
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="p-3 font-medium">Time</th>
                  <th className="p-3 font-medium">Route</th>
                  <th className="p-3 font-medium">Identifier</th>
                  <th className="p-3 font-medium">Client Key</th>
                  <th className="p-3 font-medium">Limit</th>
                  <th className="p-3 font-medium">Remote IP</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    <td className="p-3 whitespace-nowrap text-xs">
                      {new Date(e.occurred_at).toLocaleString()}
                    </td>
                    <td className="p-3 font-mono text-xs">
                      {e.route || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        {e.identifier}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-xs truncate max-w-xs" title={e.client_key || undefined}>
                      {e.client_key ? e.client_key.substring(0, 16) + '...' : '-'}
                    </td>
                    <td className="p-3 text-xs">{e.limit || '-'}</td>
                    <td className="p-3 font-mono text-xs">{e.remote_addr || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export default RateLimiting
